import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { PAGE_CONFIG } from './Config';
import EmptyPage from '../../components/EmptyPage';
import { useTranslation } from 'react-i18next';
import PageIntro from '../../components/PageIntro';
import ContentCard from '../../components/ContentCard';
import TablePagination from '../../components/Pagination';
import tableStyles from '../../styles/tableStyles';
import api from '../../api';
import { useQuery } from '@tanstack/react-query';

const Videos = () => {
    const { t } = useTranslation();
    const tr = useCallback(
        (key) => {
            return t(`videos:${key}`);
        },
        [t]
    );

    const pageConfig = useMemo(() => PAGE_CONFIG(tr), [tr]);

    const [materialTypeId, setMaterialTypeId] = useState(null);
    
    // Pagination state management
    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        search: ''
    });

    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        setParams((prev) => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (limit) => {
        if (limit < 1) return;
        setParams((prev) => ({ ...prev, limit }));
    };

    // get material types
    const { data: materialTypes } = useQuery({
        queryKey: ['materialTypes'],
        queryFn: () => api.materialTypes.getMaterialTypes(),
        select: (data) => data?.data?.data
    });

    // Use useEffect to watch for materialTypes data changes
    useEffect(() => {
        if (materialTypes && materialTypes.length > 0) {
            // Try multiple approaches to find the video material type
            const materialType = materialTypes.find((i) => 
                i.name.toLowerCase().includes('video') || 
                i.name.toLowerCase() === 'video' ||
                i.name.toLowerCase() === 'videos'
            ); 
            if (materialType) {
                setMaterialTypeId(materialType.id);
            } else {
                console.warn('No video material type found. Available types:', materialTypes.map(mt => mt.name));
            }
        }
    }, [materialTypes]);

    // Combine materialTypeId with other params
    const queryParams = useMemo(
        () => ({
            ...params,
            materialTypeId: materialTypeId
        }),
        [params, materialTypeId]
    );

    // Use useQuery with proper pagination handling
    const { data: videosResponse, isLoading } = useQuery({
        queryKey: ['videos', queryParams],
        queryFn: () => api.upload.getUploads(queryParams),
        enabled: !!materialTypeId, // Only run when materialTypeId is available
        select: (data) => data?.data
    });

    // Extract data and pagination from response
    const videos = videosResponse?.data;
    const pagination = {
        page: params.page,
        limit: params.limit,
        totalCount: videosResponse?.totalCount || 0
    };
    const totalPages = Math.ceil(pagination.totalCount / pagination.limit);

    return (
        <div>
            <PageIntro description={pageConfig.PAGE_DESCRIPTION} />
            <div className="text-sm text-gray-500 ml-2 mt-4 dark:text-gray-400">
                Showing {videos?.length} of {pagination.totalCount} videos
            </div>

            {videos && videos.length > 0 ? (
                <ContentCard data={videos} />
            ) : (
                <EmptyPage colSpan={12} isLoading={isLoading} icon={pageConfig.EMPTY_PAGE_ICON} description={pageConfig.SEARCH_DESCRIPTION} placeholder={pageConfig.SEARCH_PLACEHOLDER} loadingMessage={pageConfig.SEARCH_LOADING_MESSAGE} />
            )}

            {/* Add pagination component */}
            {videos && videos.length > 0 && <TablePagination totalPages={totalPages} page={pagination.page} limit={pagination.limit} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} tableStyles={tableStyles} />}
        </div>
    );
};

export default Videos;
